import { Link } from 'react-router-dom'

type ActionItem = {
  id: string
  item?: string
  done?: boolean
  due_date?: string
  dueDate?: string
}

type MorningViewProps = {
  firstName?: string
  eventSummary: {
    today: Array<{ id: string; time: string; title: string; tagTheme: string }>
  } | null
  latestActionItems: ActionItem[] | null
  recentDrafts: Array<{ id: string; subject: string; body: string; date: string }> | null
}

const stripHtmlToText = (value?: string) => {
  if (!value) return ''
  const withBreaks = value
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<\/li>/gi, '\n')
    .replace(/<\/h\d>/gi, '\n')
  if (typeof window !== 'undefined' && 'DOMParser' in window) {
    const doc = new DOMParser().parseFromString(withBreaks, 'text/html')
    return (doc.body.textContent || '').replace(/\u00a0/g, ' ').replace(/\r\n?/g, '\n')
  }
  return withBreaks
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+\n/g, '\n')
    .replace(/\u00a0/g, ' ')
    .replace(/\r\n?/g, '\n')
}

const MorningView = ({ firstName, eventSummary, latestActionItems, recentDrafts }: MorningViewProps) => {
  return (
    <>
      <div className="hero-card-adaptive morning">
        <div className="hero-content">
          <h1 className="hero-title">Good morning, {firstName || 'there'} 👋</h1>
          <p className="hero-subtitle">
            You have {eventSummary?.today.length || 0} calls today. 
            Bev prepped briefings and drafted follow-up emails from yesterday.
          </p>
          <div className="hero-actions">
            <button type="button" 
              className="hero-btn primary"
              onClick={() => window.dispatchEvent(new CustomEvent('toggle-voice-agent'))}
            >
              Talk to Bev
            </button>
            <Link to="/calendar?view=Day" className="hero-btn secondary">Review Your Day</Link>
          </div>
        </div>
      </div>

      <div className="home-section">
        <p className="eyebrow">Needs Your Attention</p>
        <div className="home-grid-adaptive">
          <div className="home-panel">
            <h2 className="home-highlight-title" style={{ marginBottom: 16 }}>📧 Pending from Bev</h2>
            <div className="home-action-items-list" style={{ gridTemplateColumns: '1fr' }}>
              {recentDrafts && recentDrafts.length > 0 ? (
                recentDrafts.slice(0, 3).map((draft, idx) => (
                  <Link key={`${draft.id}-${idx}`} to={`/conversation/${draft.id}`} className="home-action-item">
                    <div className="home-action-item-text" style={{ whiteSpace: 'normal' }}>
                      <strong>{draft.subject}</strong>
                      <p className="muted" style={{ 
                        margin: '4px 0 0', 
                        fontSize: '13px',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}>
                        {stripHtmlToText(draft.body)}
                      </p>
                    </div>
                    <span className="adaptive-item-badge needs-review">Needs Review</span>
                  </Link>
                ))
              ) : (
                <div className="home-action-item" style={{ cursor: 'default' }}>
                  <div className="home-action-item-text">
                    <p className="muted">No pending drafts from Bev.</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="home-panel">
            <h2 className="home-highlight-title" style={{ marginBottom: 16 }}>🎯 Daily Targets</h2>
            <div className="home-action-items-list" style={{ gridTemplateColumns: '1fr' }}>
              {latestActionItems && latestActionItems.length > 0 ? (
                latestActionItems.slice(0, 3).map(item => (
                  <Link key={item.id} to="/action-items" className="home-action-item">
                    <div className="home-action-item-text">
                      <strong>{item.item}</strong>
                      <p className="muted" style={{ margin: '4px 0 0', fontSize: '12px' }}>Due today</p>
                    </div>
                    <span className="due-date-pill is-approaching">Today</span>
                  </Link>
                ))
              ) : (
                <div className="home-action-item" style={{ cursor: 'default' }}>
                  <div className="home-action-item-text">
                    <p className="muted">No action items due today.</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="home-section">
        <p className="eyebrow">Today's Schedule</p>
        <div className="home-panel">
          <div className="home-action-items-list" style={{ gridTemplateColumns: '1fr' }}>
            {eventSummary?.today.length ? (
              eventSummary.today.map(event => (
                <Link key={event.id} to="/calendar" className="home-action-item">
                  <div className="home-action-item-text">
                    <strong>{event.title}</strong>
                    <p className="muted" style={{ margin: '4px 0 0', fontSize: '13px' }}>Briefing is ready for review</p>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span className="adaptive-item-badge done">Briefing Ready</span>
                    <span className="due-date-pill">{event.time}</span>
                  </div>
                </Link>
              ))
            ) : (
              <p className="muted">No meetings scheduled for today.</p>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default MorningView
